/**
 * Derived balance snapshot for an account — structured like the Alpaca
 * /v2/account response, broken into user-facing sections.
 *
 * We don't add balance fields to `BrokerAccount` directly because the
 * surface here is presentation-only (Balances tab + hero drawer). The
 * source of truth stays on `BrokerAccount`; this module derives the
 * twenty-odd display fields from it so the UI doesn't have to do math.
 *
 * Two variants are derivable:
 *   • `total`   — everything the account owns, including cash currently
 *                 allocated to active bots.
 *   • `exBots`  — what the user controls manually — total minus bot-
 *                 allocated cash. Mirrors the existing `buyingPowerExBots`
 *                 semantics used elsewhere in the V2 hero.
 */

import type { BrokerAccount, Bot } from "@/data/mock-data";

// ---- Public types ---------------------------------------------------------

export type BalanceMode = "total" | "exBots";

export interface BalanceSnapshot {
  /** Last market close — what the account was worth at the prior 4:00 PM ET. */
  lastClose: BalanceValues;
  /** Current live figure — intraday, post-fills. */
  current: BalanceValues;
  /** Derived one-value fields used by the margin buffer visualization. */
  margin: MarginBuffer;
  /** Which mode this snapshot was computed for. */
  mode: BalanceMode;
}

export interface BalanceValues {
  // Buying Power
  regTBuyingPower: number;
  dayTradingBuyingPower: number;
  effectiveBuyingPower: number;
  nonMarginableBuyingPower: number;

  // Margin
  initialMargin: number;
  maintenanceMargin: number;

  // Cash
  cash: number;
  cashWithdrawable: number;
  pendingTransferOut: number;
  /** `null` indicates "—" — the WHB/Alpaca UI shows an em-dash when
   *  the broker hasn't reported this field yet. */
  pendingTransferIn: number | null;

  // High Yield Cash
  cashAccruingInterest: number;
  monthlyAccruedInterest: number;
  totalAccruedInterest: number;

  // Positions
  equity: number;
  longMarketValue: number;
  shortMarketValue: number;
  positionMarketValue: number;

  // Miscellaneous
  accruedFees: number;
  heldAchDeposits: number;
  /** `null` when the broker doesn't expose this (matches Alpaca's behavior). */
  dayTradeCount: number | null;
}

export interface MarginBuffer {
  /** Total equity in the account (positions + cash). */
  portfolioValue: number;
  /** Total maintenance requirement — how much margin the broker needs held. */
  totalMaintReq: number;
  /** Margin currently being used to hold leveraged positions. */
  marginUsed: number;
  /** Margin still available to borrow. */
  marginAvailable: number;
  /** Usage ratio (marginUsed / totalMaintReq). 0 = no leverage, 1 = at limit. */
  usageRatio: number;
}

// ---- Derivation helpers ---------------------------------------------------

/**
 * Derive a balance snapshot for a single account. The mock formulas
 * mirror how the real broker API would split the numbers — we assume
 * a standard RegT margin account (25% maintenance, 50% initial) with
 * no pattern-day-trader status, which means Day Trading Buying Power
 * is zero for cash-settled accounts.
 */
export function deriveBalance(
  account: BrokerAccount,
  bots: Bot[],
  mode: BalanceMode = "total"
): BalanceSnapshot {
  // Bot-attributed slices — used to compute "exBots" view.
  //
  // Sergey 2026-04-30 formulation (replaces the earlier
  // allocation-based approximations):
  //
  //   • botCash       — Σ bot.cash. Subtract from account cash to get
  //                     the user-controlled cash slice.
  //   • botEquity     — Σ bot.currentValue (≡ bot.equity = stocks + cash).
  //                     Subtract from account equity to get user equity.
  //   • botStocks     — Σ (bot.currentValue − bot.cash). Subtract from
  //                     account LMV. Falls out from botEquity − botCash
  //                     when both are populated, but computed
  //                     per-bot here so a single bot's missing field
  //                     doesn't poison the whole sum.
  //   • botBuyingPower — Σ bot.buyingPower. Subtract from broker-
  //                      supplied account.buyingPower for Effective BP
  //                      excl. bots.
  //
  // Bots whose `cash` overlay hasn't landed yet (cache-miss / in-
  // flight /bots/{id}) contribute 0 — better to under-deduct (more
  // conservative for the user's headroom) than to fabricate a number.
  const accountBots = bots.filter((b) => b.accountId === account.id);
  const botCash = accountBots.reduce(
    (s, b) =>
      s + (typeof b.cash === "number" && Number.isFinite(b.cash) ? b.cash : 0),
    0
  );
  const botEquity = accountBots.reduce(
    (s, b) => s + (Number.isFinite(b.currentValue) ? b.currentValue : 0),
    0
  );
  const botStocksValue = accountBots.reduce((s, b) => {
    const cv = Number.isFinite(b.currentValue) ? b.currentValue : 0;
    const c = typeof b.cash === "number" && Number.isFinite(b.cash) ? b.cash : 0;
    return s + Math.max(0, cv - c);
  }, 0);
  const botBuyingPower = accountBots.reduce(
    (s, b) => s + (Number.isFinite(b.buyingPower) ? b.buyingPower : 0),
    0
  );

  // Base figures — already present on BrokerAccount.
  const cashFull = account.cashValue;
  const stocksFull = account.stocksValue;
  // Broker-supplied effective buying power. WHB API exposes a single
  // `portfolio.buyingPower` we treat as the canonical Effective BP —
  // anything we re-derive locally won't match Alpaca's number because
  // we don't have visibility into the broker's per-asset margin
  // requirements (different stocks have different reqs, ETFs differ
  // from equities, etc.). Use it directly when present.
  const buyingPowerFull =
    typeof account.buyingPower === "number" && account.buyingPower > 0
      ? account.buyingPower
      : null;

  // exBots: subtract per-bot field-matched sums. Sergey 2026-04-30:
  //
  //   cashExBots   = account.cash − Σ bot.cash
  //   stocksExBots = max(0, account.LMV − Σ bot.stocksValue)
  //   equityExBots = account.equity − Σ bot.equity        (≡ Σ bot.currentValue)
  //
  // Cash is left signed (negative is valid on margin). LMV is clamped
  // at 0 — a long market value can't go below zero. Equity does NOT
  // re-derive from cash+positions; the user's spec is "exclude exactly
  // the bots' equity slice from the broker's total equity". This
  // preserves accruals / fees / rounding the broker bakes into the
  // total but isn't visible to us per-bot.
  const cash = mode === "exBots" ? cashFull - botCash : cashFull;
  const positions =
    mode === "exBots" ? Math.max(0, stocksFull - botStocksValue) : stocksFull;
  // Equity in TOTAL mode prefers the broker-supplied portfolioValue
  // (matches the page hero exactly — accounts for fees / accruals /
  // rounding the broker applies that aren't in the cash + LMV sum).
  // Sergey 2026-04-28: the margin buffer was showing $86,556.95 while
  // the hero showed $86,574.06 — same concept, two slightly different
  // local sums. Single broker source ends that.
  const totalEquity =
    typeof account.portfolioValue === "number" &&
    Number.isFinite(account.portfolioValue) &&
    account.portfolioValue > 0
      ? account.portfolioValue
      : cashFull + stocksFull;
  const equity =
    mode === "exBots" ? Math.max(0, totalEquity - botEquity) : totalEquity;

  // Margin figures — RegT 50% initial, 25% maintenance on long stock.
  // Alpaca actually reports per-position requirements (some at 30%+
  // for volatile names) so our maintenance is approximate; initial
  // matches Alpaca's number to within a few dollars.
  const initialMargin = positions * 0.5;
  const maintenanceMargin = positions * 0.25;

  // ---- BP figures (Sergey 2026-04-30 — derived from per-bot fields) -------
  //
  // RegT Buying Power — `2 × (equity − initial_margin)`. Verified
  // against Alpaca's RegT BP exactly in TOTAL mode (equity 86,652 −
  // initial 52,812 = 33,840 × 2 = $67,680, matches the broker's number
  // to the cent). exBots derives from the *same formula* but with
  // `equity` and `initialMargin` already deducted by their bot
  // contributions — semantically: "treat the bot equity as if it
  // weren't here".
  const regTBP = Math.max(0, 2 * (equity - initialMargin));

  // Effective BP — broker-supplied for total mode (their per-asset
  // margin requirements differ from a flat 50% so re-derivation
  // doesn't match Alpaca to the cent). For exBots, subtract Σ
  // bot.buyingPower — semantically "the slice of BP currently
  // earmarked by the bots".
  const effectiveTotal =
    buyingPowerFull ?? Math.max(0, cashFull + stocksFull * 0.5);
  const effective =
    mode === "exBots"
      ? Math.max(0, effectiveTotal - botBuyingPower)
      : effectiveTotal;
  const dayTradingBP = effective;

  // FINRA $2000 minimum equity buffer — Alpaca's "Cash Withdrawable"
  // and "Non-Marginable BP" both use this. Same formula in both
  // modes; equity & initialMargin already reflect the mode.
  const FINRA_MARGIN_MIN = 2000;
  const nonMarginable = Math.max(
    0,
    equity - initialMargin - FINRA_MARGIN_MIN
  );
  const cashWithdrawableValue = nonMarginable;

  // High Yield Cash — small sweep portion earning interest. Skip
  // entirely on negative-cash margin accounts; otherwise the sweep
  // would be capped at a negative number and propagate through the
  // accrued-interest fields as nonsensical negatives (Sergey
  // 2026-04-28 caught -$18k showing as Cash Accruing Interest).
  const sweep =
    cashFull > 0 ? Math.min(cashFull, cashHash(account.id, 2800, 7800)) : 0;
  const monthlyInterest = +(sweep * 0.045 / 12).toFixed(2); // 4.5% APY
  const totalAccrued = +(monthlyInterest * 5.7).toFixed(2); // several months compounded

  // Prior close values — a small deterministic delta from current so
  // the two-column "Last close / Current" table reads believably.
  const closeDrift = driftFromHash(account.id);
  const lastCloseCash = +(cash * (1 - closeDrift.cashDelta)).toFixed(2);
  const lastCloseEquity = +(equity * (1 - closeDrift.equityDelta)).toFixed(2);
  const lastClosePositions = lastCloseEquity - lastCloseCash;
  const lastCloseRegTBP = lastCloseCash + lastClosePositions * 1.5;
  const lastCloseEffective = lastCloseCash + lastClosePositions * 0.5;

  const lastClose: BalanceValues = {
    regTBuyingPower: lastCloseRegTBP,
    dayTradingBuyingPower: 0,
    effectiveBuyingPower: lastCloseEffective,
    nonMarginableBuyingPower: lastCloseCash,
    initialMargin: lastClosePositions * 0.5,
    maintenanceMargin: lastClosePositions * 0.25,
    cash: lastCloseCash,
    cashWithdrawable: lastCloseCash * 0.98,
    pendingTransferOut: 0,
    pendingTransferIn: null,
    cashAccruingInterest: sweep,
    monthlyAccruedInterest: monthlyInterest,
    totalAccruedInterest: totalAccrued,
    equity: lastCloseEquity,
    longMarketValue: lastClosePositions,
    shortMarketValue: 0,
    positionMarketValue: lastClosePositions,
    accruedFees: 0,
    heldAchDeposits: 0,
    dayTradeCount: null
  };

  const current: BalanceValues = {
    regTBuyingPower: regTBP,
    dayTradingBuyingPower: dayTradingBP,
    effectiveBuyingPower: effective,
    nonMarginableBuyingPower: nonMarginable,
    initialMargin,
    maintenanceMargin,
    cash,
    cashWithdrawable: cashWithdrawableValue,
    pendingTransferOut: 0,
    pendingTransferIn: null,
    cashAccruingInterest: sweep,
    monthlyAccruedInterest: monthlyInterest,
    totalAccruedInterest: totalAccrued,
    equity,
    longMarketValue: positions,
    shortMarketValue: 0,
    positionMarketValue: positions,
    accruedFees: 0,
    heldAchDeposits: 0,
    dayTradeCount: null
  };

  // Margin buffer — Robinhood-style usage indicator.
  //
  //   • marginUsed       — current cash debit; |negative cash| in a
  //                        margin account. Zero for unleveraged.
  //   • marginAvailable  — effective buying power (what's actually
  //                        deployable now). The previous formula
  //                        subtracted `marginUsed` from `effective`
  //                        and reported the difference, which
  //                        contradicted the same number elsewhere
  //                        on the page (Sergey 2026-04-28: "why some
  //                        numbers under horizontal status line
  //                        differ with other numbers Available?").
  //                        Effective BP is by definition net of
  //                        existing margin debt — no second deduction.
  //   • usageRatio       — maintenance_margin / equity. 0 = no margin
  //                        risk, 1 = at margin call (equity =
  //                        maintenance req). Healthy accounts sit at
  //                        0.2–0.4. The previous formula compared
  //                        marginUsed to totalMaintReq, which are
  //                        unrelated quantities — that's why the
  //                        bar lit up "Approaching limit" amber on
  //                        a perfectly healthy account ($86k equity,
  //                        $26k maint = ratio of 0.30, comfortable
  //                        green).
  const marginUsed = Math.max(0, -cash);
  const marginAvailable = effective;
  const totalMaintReq = maintenanceMargin;
  const usageRatio =
    equity <= 0 ? 1 : Math.min(1, totalMaintReq / equity);

  return {
    lastClose,
    current,
    margin: {
      portfolioValue: equity,
      totalMaintReq,
      marginUsed,
      marginAvailable,
      usageRatio
    },
    mode
  };
}

/**
 * Combine multiple per-account balance snapshots into one aggregate.
 * Fields that sum cleanly (cash, market value, margin numbers) are
 * summed; ratio-style fields (usageRatio) are re-derived from the
 * summed totals so they reflect the aggregate, not an average.
 *
 * `pendingTransferIn` and `dayTradeCount` flip to `null` when any
 * contributor reports `null` — preserves the "broker didn't tell us"
 * semantics rather than lying with a 0.
 */
export function aggregateBalances(
  snapshots: BalanceSnapshot[]
): BalanceSnapshot {
  if (snapshots.length === 0) {
    throw new Error("aggregateBalances: empty list");
  }
  if (snapshots.length === 1) return snapshots[0];

  const sumVals = (pick: (v: BalanceValues) => number): number =>
    snapshots.reduce((s, snap) => s + pick(snap.current), 0);

  const sumCloseVals = (pick: (v: BalanceValues) => number): number =>
    snapshots.reduce((s, snap) => s + pick(snap.lastClose), 0);

  const anyNullIn = snapshots.some((s) => s.current.pendingTransferIn === null);
  const anyNullDay = snapshots.some((s) => s.current.dayTradeCount === null);
  const anyNullInClose = snapshots.some((s) => s.lastClose.pendingTransferIn === null);
  const anyNullDayClose = snapshots.some((s) => s.lastClose.dayTradeCount === null);

  const current: BalanceValues = {
    regTBuyingPower: sumVals((v) => v.regTBuyingPower),
    dayTradingBuyingPower: sumVals((v) => v.dayTradingBuyingPower),
    effectiveBuyingPower: sumVals((v) => v.effectiveBuyingPower),
    nonMarginableBuyingPower: sumVals((v) => v.nonMarginableBuyingPower),
    initialMargin: sumVals((v) => v.initialMargin),
    maintenanceMargin: sumVals((v) => v.maintenanceMargin),
    cash: sumVals((v) => v.cash),
    cashWithdrawable: sumVals((v) => v.cashWithdrawable),
    pendingTransferOut: sumVals((v) => v.pendingTransferOut),
    pendingTransferIn: anyNullIn
      ? null
      : sumVals((v) => v.pendingTransferIn ?? 0),
    cashAccruingInterest: sumVals((v) => v.cashAccruingInterest),
    monthlyAccruedInterest: sumVals((v) => v.monthlyAccruedInterest),
    totalAccruedInterest: sumVals((v) => v.totalAccruedInterest),
    equity: sumVals((v) => v.equity),
    longMarketValue: sumVals((v) => v.longMarketValue),
    shortMarketValue: sumVals((v) => v.shortMarketValue),
    positionMarketValue: sumVals((v) => v.positionMarketValue),
    accruedFees: sumVals((v) => v.accruedFees),
    heldAchDeposits: sumVals((v) => v.heldAchDeposits),
    dayTradeCount: anyNullDay
      ? null
      : sumVals((v) => v.dayTradeCount ?? 0)
  };

  const lastClose: BalanceValues = {
    regTBuyingPower: sumCloseVals((v) => v.regTBuyingPower),
    dayTradingBuyingPower: sumCloseVals((v) => v.dayTradingBuyingPower),
    effectiveBuyingPower: sumCloseVals((v) => v.effectiveBuyingPower),
    nonMarginableBuyingPower: sumCloseVals((v) => v.nonMarginableBuyingPower),
    initialMargin: sumCloseVals((v) => v.initialMargin),
    maintenanceMargin: sumCloseVals((v) => v.maintenanceMargin),
    cash: sumCloseVals((v) => v.cash),
    cashWithdrawable: sumCloseVals((v) => v.cashWithdrawable),
    pendingTransferOut: sumCloseVals((v) => v.pendingTransferOut),
    pendingTransferIn: anyNullInClose
      ? null
      : sumCloseVals((v) => v.pendingTransferIn ?? 0),
    cashAccruingInterest: sumCloseVals((v) => v.cashAccruingInterest),
    monthlyAccruedInterest: sumCloseVals((v) => v.monthlyAccruedInterest),
    totalAccruedInterest: sumCloseVals((v) => v.totalAccruedInterest),
    equity: sumCloseVals((v) => v.equity),
    longMarketValue: sumCloseVals((v) => v.longMarketValue),
    shortMarketValue: sumCloseVals((v) => v.shortMarketValue),
    positionMarketValue: sumCloseVals((v) => v.positionMarketValue),
    accruedFees: sumCloseVals((v) => v.accruedFees),
    heldAchDeposits: sumCloseVals((v) => v.heldAchDeposits),
    dayTradeCount: anyNullDayClose
      ? null
      : sumCloseVals((v) => v.dayTradeCount ?? 0)
  };

  const totalMaintReq = current.maintenanceMargin;
  const marginUsed = snapshots.reduce((s, snap) => s + snap.margin.marginUsed, 0);
  // marginAvailable mirrors current.effectiveBuyingPower so the
  // headline number matches the buffer-row "Available". Sergey
  // 2026-04-28: the old `Σ snapshot.marginAvailable` summed the
  // already-broken per-account number from before the fix.
  const marginAvailable = current.effectiveBuyingPower;
  const portfolioValue = current.equity;
  // Aggregate usageRatio = totalMaintReq / equity (same formula as
  // the per-account version), recomputed from the summed totals so
  // All-Live's headline reflects the *aggregate* margin tightness,
  // not an average of per-account ratios.
  const usageRatio =
    portfolioValue <= 0 ? 1 : Math.min(1, totalMaintReq / portfolioValue);

  return {
    lastClose,
    current,
    margin: {
      portfolioValue,
      totalMaintReq,
      marginUsed,
      marginAvailable,
      usageRatio
    },
    mode: snapshots[0].mode
  };
}

// ---- Tiny deterministic helpers ------------------------------------------

/**
 * Hash a string into a number within `[min, max]`. Used so demo
 * figures (sweep balances, drift ratios) are stable across renders
 * and reads but differ believably across accounts.
 */
function cashHash(s: string, min: number, max: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const range = max - min;
  return min + (h % range);
}

function driftFromHash(id: string): { cashDelta: number; equityDelta: number } {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 131 + id.charCodeAt(i)) >>> 0;
  // Keep drifts small (±2%) so the two columns look like a real day's diff.
  const cashDelta = ((h % 400) / 10000) - 0.02;
  const equityDelta = (((h >> 3) % 400) / 10000) - 0.02;
  return { cashDelta, equityDelta };
}
